// src/utils/pricingFromDb.js
// Versione: 2025-10-05a
// Compatibile con ListinoAdmin / get_listino.php
// - Robusta a varianti di schema (base/listino_base/data, params/parametri, accessori/listino_accessori, accessori_tipo/listino_accessori_tipo)
// - Alias e fallback per zanzariera / allargatori (cruciali)
// - Bonus coprifili gestito via params.{coprifili_bonus_n, coprifili_bonus_costo}
// - Opzione facoltativa autoDueColori per applicare automaticamente la una-tantum doppio colore
// - **NEW**: normalizzazione chiavi tipo (UPPERCASE, rimozione spazi e . _ -) per evitare mismatch

// ==========================================
// Helpers condivisi
// ==========================================
const TKEY = (v) =>
  String(v ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s._-]+/g, ''); // "F2 A" => "F2A", "porta-finestra" => "PORTAFINESTRA"

const num = (x, d = 0) => {
  if (x == null || x === '') return d;
  if (typeof x === 'string') {
    // Accetta "1,23", "1 234,56", "1.234,56", "1,234.56"
    let s = x.replace(/\s+/g, '').trim();
    if (/,/.test(s)) {
      // virgola come separatore decimale → rimuovi i punti migliaia e converti la virgola
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      // punto come separatore decimale → rimuovi eventuali migliaia con virgola
      s = s.replace(/,/g, '');
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : d;
  }
  const n = Number(x);
  return Number.isFinite(n) ? n : d;
};

const safeStr = (v) => (v == null ? '' : String(v));
const trimLower = (v) => safeStr(v).trim().toLowerCase();
const flagOn = (v) => {
  if (v === true) return true;
  const s = String(v).trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'si' || s === 'sì' || s === 'yes';
};

function round2(x) {
  return Math.round((Number(x) || 0) * 100) / 100;
}

// ==========================================
// FETCH LISTINO (compatibile con ListinoAdmin)
// ==========================================
export async function fetchListino(url = '/preventivi/get_listino.php') {
  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) throw new Error('Errore caricando listino');

  let j;
  try {
    j = await res.json();
  } catch {
    throw new Error('Risposta listino non valida (JSON)');
  }

  if (!j || j.status !== 'OK') {
    throw new Error(j?.message || 'Listino non disponibile');
  }

  // Atteso: { status:'OK', base:[...], params:{...}, accessori_tipo?:[...] }
  return normalizeListino(j);
}

// ==========================================
// NORMALIZZAZIONE VERSO UN MODELLO STABILE
// (comprende anche la normalizzazione delle chiavi TIPO)
// ==========================================
export function normalizeListino(raw) {
  // --- BASE PER TIPO ---
  const baseRows = raw.base || raw.listino_base || raw.data || [];
  const baseByTipo = {};
  (baseRows || []).forEach((r) => {
    const tipo =
      r.tipo || r.type || r.name || r.codice || r.TIPO || r.Tipologia || null;
    if (!tipo) return;
    const key = TKEY(tipo);
    baseByTipo[key] = {
      prezzo: num(r.prezzo_base ?? r.prezzo ?? r.price, 0),
      coprifili: num(r.coprifili_inclusi ?? r.coprifili ?? 0),
      img: r.img || null,
    };
  });

  // --- PARAMETRI GLOBALI ---
  const p = raw.params || raw.parametri || {};
  const params = {
    // Coprifili
    coprifili_prezzo: num(p.coprifili_prezzo, 80),
    coprifili_bonus_n: num(p.coprifili_bonus_n, 5),
    coprifili_bonus_costo: num(p.coprifili_bonus_costo, 80),

    // Accessori fissi (cad.)
    acc_zanzariera: num(p.acc_zanzariera, 350),
    acc_allargatori: num(p.acc_allargatori, 100),

    // Scuri / Persiane per gruppi (fallback se manca tabella accessori_tipo)
    scuri_g1: num(p.scuri_g1, 600),
    scuri_g2: num(p.scuri_g2, 1200),
    scuri_g3: num(p.scuri_g3, 1800),
    pers_g1: num(p.pers_g1, 800),
    pers_g2: num(p.pers_g2, 1400),
    pers_g3: num(p.pers_g3, 2000),

    // Moltiplicatori
    factor_zona_f: num(p.factor_zona_f, 1.25),
    factor_tapparella: num(p.factor_tapparella, 1.2),
    factor_cassonetto: num(p.factor_cassonetto, 1.3),
    factor_colore_premium: num(p.factor_colore_premium, 1.2),

    // Una tantum e fissi
    due_colori_once: num(p.due_colori_once, 400),
    costi_fissi_preventivo: num(p.costi_fissi_preventivo, 2000),
    costi_fissi_preventivo_fino3: num(p.costi_fissi_preventivo_fino3, 1500),
  };

  // --- ACCESSORI UNITARI (map chiave → costo) ---
  const accRows = raw.accessori || raw.listino_accessori || [];
  const acc = {};
  const keyAlias = (k) => {
    const s = trimLower(k);
    if (!s) return '';
    if (['zanza', 'zanzariera', 'zanzariere'].includes(s)) return 'zanza';
    if (['allar', 'allargatori', 'allargatore'].includes(s)) return 'allar';
    return s;
  };

  (accRows || []).forEach((r) => {
    const rawKey = r.chiave || r.key || r.accessorio || r.name || '';
    const key = keyAlias(rawKey);
    if (!key) return;
    acc[key] = num(r.costo_unitario ?? r.valore ?? r.costo ?? r.price, 0);
  });

  // Fallback/alias per quello che ci serve davvero
  if (acc.zanza == null && acc.zanzariera != null) acc.zanza = num(acc.zanzariera);
  if (acc.zanza == null) acc.zanza = params.acc_zanzariera; // ← **cruciale per la zanzariera**
  if (acc.allar == null && acc.allargatori != null) acc.allar = num(acc.allargatori);
  if (acc.allar == null) acc.allar = params.acc_allargatori;

  // --- ACCESSORI PER GRUPPO (A/B/C) ---
  const accTipoRows = raw.accessori_tipo || raw.listino_accessori_tipo || [];
  const accTipo = { scuri: {}, pers: {} };

  const normAccTipo = (s) => {
    s = trimLower(s);
    if (['scuro', 'scuri', 'imposte'].includes(s)) return 'scuri';
    if (['pers', 'persiana', 'persiane'].includes(s)) return 'pers';
    return s; // accetta comunque chiavi custom
  };

  if (Array.isArray(accTipoRows) && accTipoRows.length) {
    accTipoRows.forEach((r) => {
      const a = normAccTipo(r.accessorio || r.tipo || '');
      const g = safeStr(r.gruppo || r.group || r.livello || '').trim().toUpperCase(); // A/B/C
      const c = num(r.costo_unitario ?? r.valore ?? r.costo ?? r.price, 0);
      if (!a || !g) return;
      if (!accTipo[a]) accTipo[a] = {};
      accTipo[a][g] = c;
    });
  } else {
    // fallback dai params
    accTipo.scuri = { A: params.scuri_g1, B: params.scuri_g2, C: params.scuri_g3 };
    accTipo.pers  = { A: params.pers_g1,  B: params.pers_g2,  C: params.pers_g3  };
  }

  return { baseByTipo, params, acc, accTipo };
}

// ==========================================
// LOGICA DI RAGGRUPPAMENTO TIPI → GRUPPI
// ==========================================
export function tipoToGroup(tipo) {
  const t = TKEY(tipo); // già uppercase/senza spazi
  // A = F1A / PF1A / FIX / PORTA
  if (['F1A', 'PF1A', 'FIX', 'PORTA'].includes(t)) return 'A';
  // B = F2A / PF2A
  if (['F2A', 'PF2A'].includes(t)) return 'B';
  // C = tutto il resto
  return 'C';
}

// Bianco/Avorio gratuiti, tutto il resto = premium
export function isPremiumColor(colorId, colors) {
  if (!colorId) return false;
  const idNum = Number(colorId);
  const c = Array.isArray(colors)
    ? colors.find((x) => Number(x.id) === idNum)
    : null;
  if (!c) return false;
  const name = (c.name || '').trim().toLowerCase();
  return !(name === 'bianco' || name === 'avorio');
}

// ==========================================
// PREZZO DI UNA RIGA (unit price * qty)
// ==========================================
export function priceLine(riga, listino, colors) {
  if (!listino || !riga) return 0;

  const { baseByTipo, params, acc, accTipo } = listino;
  const tipoKey = TKEY(riga.tipo || '');
  const info = baseByTipo[tipoKey] || { prezzo: 0, coprifili: 0 };
  const qty = Math.max(1, Number(riga.qty) || 1);

  // 1) Base + coprifili inclusi * prezzo coprifilo
  let prezzoUnit =
    Number(info.prezzo || 0) +
    Number(info.coprifili || 0) * Number(params.coprifili_prezzo || 0);

  // 2) Moltiplicatori (sull’infisso + coprifili)
  const f = riga.flags || {};
  if (flagOn(f.zonaF)) prezzoUnit *= Number(params.factor_zona_f || 1);
  if (flagOn(f.tapparella) || flagOn(f.tapp)) prezzoUnit *= Number(params.factor_tapparella || 1);
  if (flagOn(f.cassonetto) || flagOn(f.cass)) prezzoUnit *= Number(params.factor_cassonetto || 1);

  // 3) Colore premium (se almeno uno dei due è premium)
  const premium =
    isPremiumColor(riga.color1Id, colors) ||
    isPremiumColor(riga.color2Id, colors);
  if (premium) prezzoUnit *= Number(params.factor_colore_premium || 1);

  // 4) Accessori per pezzo
  const gruppo = tipoToGroup(tipoKey);
  const scuriUnit = accTipo.scuri?.[gruppo] ?? 0;
  const persUnit  = accTipo.pers?.[gruppo]  ?? 0;
  const zanzaUnit = acc.zanza ?? 0; // ← prende da params se mancano le tabelle
  const allarUnit = acc.allar ?? 0;

  const accTotUnit =
    (Number(riga.scuri) || 0) * scuriUnit +
    (Number(riga.pers)  || 0) * persUnit +
    (Number(riga.zanza) || 0) * zanzaUnit +
    (Number(riga.allar) || 0) * allarUnit;

  const unitTotal = prezzoUnit + accTotUnit;

  return round2(unitTotal * qty);
}

// (Facoltativo) Breakdown utile per debug/strumenti
export function priceLineBreakdown(riga, listino, colors) {
  if (!listino || !riga) return { unit: 0, qty: 0, total: 0 };
  const { baseByTipo, params, acc, accTipo } = listino;
  const tipoKey = TKEY(riga.tipo || '');
  const info = baseByTipo[tipoKey] || { prezzo: 0, coprifili: 0 };
  const qty = Math.max(1, Number(riga.qty) || 1);

  let base = Number(info.prezzo || 0);
  const coprifiliUnit = Number(info.coprifili || 0) * Number(params.coprifili_prezzo || 0);
  let mul = 1;

  const f = riga.flags || {};
  if (flagOn(f.zonaF))      mul *= Number(params.factor_zona_f || 1);
  if (flagOn(f.tapparella) || flagOn(f.tapp)) mul *= Number(params.factor_tapparella || 1);
  if (flagOn(f.cassonetto) || flagOn(f.cass)) mul *= Number(params.factor_cassonetto || 1);

  const premium =
    isPremiumColor(riga.color1Id, colors) ||
    isPremiumColor(riga.color2Id, colors);
  if (premium) mul *= Number(params.factor_colore_premium || 1);

  const gruppo = tipoToGroup(tipoKey);
  const scuriUnit = accTipo.scuri?.[gruppo] ?? 0;
  const persUnit  = accTipo.pers?.[gruppo]  ?? 0;
  const zanzaUnit = acc.zanza ?? 0;
  const allarUnit = acc.allar ?? 0;

  const accQuant = {
    scuri: Number(riga.scuri) || 0,
    pers:  Number(riga.pers)  || 0,
    zanza: Number(riga.zanza) || 0,
    allar: Number(riga.allar) || 0,
  };
  const accUnit = {
    scuri: scuriUnit,
    pers:  persUnit,
    zanza: zanzaUnit,
    allar: allarUnit,
  };

  const unitPrimaAcc = (base + coprifiliUnit) * mul;
  const accTotUnit =
    accQuant.scuri * scuriUnit +
    accQuant.pers  * persUnit  +
    accQuant.zanza * zanzaUnit +
    accQuant.allar * allarUnit;

  const unit = unitPrimaAcc + accTotUnit;
  const total = round2(unit * qty);

  return {
    tipo: tipoKey,
    qty,
    base: round2(base),
    coprifiliUnit: round2(coprifiliUnit),
    moltiplicatore: round2(mul),
    premiumApplied: premium,
    unitPrimaAcc: round2(unitPrimaAcc),
    accUnit,
    accQuant,
    accTotUnit: round2(accTotUnit),
    unit: round2(unit),
    total,
  };
}

// ==========================================
// PREZZO TOTALE PREVENTIVO
// ==========================================
export function pricePreventivo(
  righe,
  listino,
  colors,
  {
    dueColoriOnce = false, // mantieni gestione manuale identica alla tua
    autoDueColori = false, // facoltativo: se true, aggiunge una tantum se >0 righe con color1Id != color2Id
  } = {}
) {
  if (!listino) {
    return {
      perRiga: [],
      subtotale: 0,
      fissi: 0,
      dueColoriOnce: 0,
      coprifiliTot: 0,
      bonusCoprifili: 0,
      totale: 0,
    };
  }
  const { baseByTipo, params } = listino;

  const perRiga = (righe || []).map((r) => priceLine(r, listino, colors));
  const subtotale = perRiga.reduce((a, b) => a + b, 0);

  // Costi fissi (<= 3 finestre usa il “fino3”)
  const righeNonVuote = (righe || []).filter((r) => r && r.tipo).length;
  const fissi =
    righeNonVuote <= 3
      ? Number(params.costi_fissi_preventivo_fino3 || 0)
      : Number(params.costi_fissi_preventivo || 0);

  // Una tantum doppio colore
  const hasDoppioCol = () =>
    (righe || []).some(
      (r) => r && r.color1Id && r.color2Id && String(r.color1Id) !== String(r.color2Id)
    );
  const applyDueCol = dueColoriOnce || (autoDueColori && hasDoppioCol());
  const dueColSupp = applyDueCol ? Number(params.due_colori_once || 0) : 0;

  // Bonus coprifili (ogni N coprifili inclusi si aggiunge il costo bonus)
  const coprifiliTot = (righe || []).reduce((sum, r) => {
    const key = TKEY(r?.tipo || '');
    const inf = baseByTipo[key];
    const q = Math.max(1, Number(r?.qty) || 1);
    return sum + (Number(inf?.coprifili || 0) * q);
  }, 0);

  const n = Number(params.coprifili_bonus_n || 0);
  const step = n > 0 ? n : Infinity;
  const bonusPacks = step === Infinity ? 0 : Math.floor(coprifiliTot / step);
  const bonusCoprifili = bonusPacks * Number(params.coprifili_bonus_costo || 0);

  const totale = round2(subtotale + fissi + dueColSupp + bonusCoprifili);

  return {
    perRiga: perRiga.map(round2),
    subtotale: round2(subtotale),
    fissi: round2(fissi),
    dueColoriOnce: round2(dueColSupp),
    coprifiliTot,
    bonusCoprifili: round2(bonusCoprifili),
    totale,
  };
}
